import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { DialogState } from 'ariakit/dialog'
import {
  Button,
  csx,
  IconCopySimple,
  Paragraph,
  Stack,
  Text,
} from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { CustomModal } from '@/components/CustomModal'
import { FullPageSpinner } from '@/components/FullPageSpinner'
import { useIndexerLog } from '@/components/products-listing/IndexedInfoModal/hooks/useIndexerLog'
import type { Product } from '@/types/product'

import * as styles from './IndexedInfoModal.styles'

interface IndexedInfoModalProps {
  state: DialogState
  item: Product
}

export function IndexedInfoModal(props: IndexedInfoModalProps) {
  const { state, item } = props
  const { formatMessage } = useIntl()
  const { locale } = useAdmin()

  const lastUpdatedDate = useMemo(() => {
    return new Date(item.lastUpdateDate).toLocaleDateString(locale, {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })
  }, [item.lastUpdateDate, locale])

  const { data: log = '', isLoading } = useIndexerLog(item.id, state.mounted)

  const handleCopyLog = () => navigator.clipboard.writeText(log)

  return (
    <CustomModal
      state={state}
      title={formatMessage({
        id: 'admin/admin.catalog.indexed-log-tab-label',
        defaultMessage: 'Indexed Log',
      })}
      subTitle={
        <Stack
          direction="row"
          space="$space-3"
          className={csx(styles.titleContainer)}
        >
          <Text
            variant="action2"
            className={csx({ marginTop: '1px', fontWeight: '400' })}
          >
            <FormattedMessage
              id="admin/admin.catalog.last-update-label"
              defaultMessage="Last update: {date}"
              values={{ date: lastUpdatedDate }}
            />
          </Text>
        </Stack>
      }
      customAction={
        <Stack
          direction="row"
          space="$space-3"
          className={csx(styles.titleContainer)}
        >
          <Button
            variant="neutralTertiary"
            size="normal"
            onClick={handleCopyLog}
          >
            <IconCopySimple />
            {formatMessage({
              id: 'admin/admin.catalog.copy-label',
              defaultMessage: 'Copy',
            })}
          </Button>
        </Stack>
      }
    >
      <IndexerLog isLoading={isLoading} log={log} />
    </CustomModal>
  )
}

interface IndexerLogProps {
  log: string
  isLoading: boolean
}

function IndexerLog(props: IndexerLogProps) {
  const { isLoading, log } = props

  return (
    <div className={csx(styles.indexerLogContainer)}>
      <Paragraph className={csx(styles.indexerLog)}>
        {isLoading ? <FullPageSpinner maxHeight="400px" /> : log}
      </Paragraph>
    </div>
  )
}
